'use client';

import { ArrowIcon } from '@fastfive-dev/fastfive-design-system/icons/arrow';

import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';

const SCROLL_DURATION_MS = 300;

/**
 * 컨테이너 스크롤을 직접 tween 한다.
 *
 * `scrollTo({ behavior: 'smooth' })` 를 쓰지 않는 이유 — transform 이 걸린 fixed 조상
 * (지점 상세 오버레이 등) 안의 스크롤러에서는 Chrome 이 스크롤 애니메이션을 무시해
 * 아무 일도 일어나지 않는다. window 스크롤은 영향이 없어 네이티브 동작을 그대로 쓴다.
 */
function tweenScrollToTop(el: Element) {
  const start = el.scrollTop;
  if (start <= 0) return;
  const startedAt = performance.now();
  const step = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / SCROLL_DURATION_MS);
    const eased = 1 - (1 - progress) ** 3;
    el.scrollTop = start * (1 - eased);
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

type Props = {
  /**
   * 스크롤을 되돌릴 대상의 셀렉터. 미지정 시 window.
   * 전체화면 오버레이처럼 window 가 아닌 내부 컨테이너가 스크롤되는 화면에서 사용한다.
   */
  targetSelector?: string;
};

export function ScrollToTopButton({ targetSelector }: Props = {}) {
  const handleClick = () => {
    if (typeof window === 'undefined') return;
    const target = targetSelector
      ? document.querySelector(targetSelector)
      : null;
    if (target) {
      tweenScrollToTop(target);
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="맨 위로 이동"
      className={`shadow-100 desktop:size-40 flex size-32 items-center justify-center rounded-full border border-[#DCDCDC] bg-gray-100 hover:bg-[rgba(255,255,255,0.75)] hover:bg-[linear-gradient(rgba(133,153,183,0.05),rgba(133,153,183,0.05))]`}
      {...gtmClick(GTM_EVENT.FLOATING, '맨 위로 이동')}
    >
      <ArrowIcon size={20} direction="up" className="text-gray-900" />
    </button>
  );
}
